/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import { RevokeCustomerTokenModel } from '@/auth/data/models';
import { fetchGraphQl, removeFetchGraphQlHeader } from '../fetch-graphql';
import { REVOKE_CUSTOMER_TOKEN } from './graphql/revokeCustomerToken.graphql';
import { handleNetworkError } from '@/auth/lib/network-error';
import { transformRevokeCustomerToken } from '@/auth/data/transforms';
import { RevokeCustomerTokenResponse } from '@/auth/types/api/revokeCustomerToken.types';
import { deleteCookie } from '@/auth/lib/cookieUtils';
import { events } from '@adobe-commerce/event-bus';
import { COOKIE_NAMES } from '@/auth/configs/cookieConfigs';
import { EventsList, publishEvents } from '@/auth/lib/acdl';
import { verifyToken } from '@/auth/api/verifyToken';
import { config } from '../initialize';
import { emitAuthGroupIdEvent } from '@/auth/lib/emitAuthGroupId';

export const revokeCustomerToken =
  async (): Promise<RevokeCustomerTokenModel> => {
    const { authHeaderConfig } = config.getConfig();


    return await fetchGraphQl(REVOKE_CUSTOMER_TOKEN, {
      method: 'POST',
    })
      .then(async (response: RevokeCustomerTokenResponse) => {
        const transformData = transformRevokeCustomerToken(response);

        if (transformData?.success) {
          [
            COOKIE_NAMES.auth_dropin_user_token,
            COOKIE_NAMES.auth_dropin_firstname,
          ].forEach((name) => {
            deleteCookie(name);
          });

          removeFetchGraphQlHeader(authHeaderConfig.header);
          await emitAuthGroupIdEvent();
          events.emit('authenticated', false);

          publishEvents(EventsList.SIGN_OUT, {});
        } else {
          const errorMessage = `
          ERROR revokeCustomerToken: ${transformData.message}`;
          console.error(errorMessage);

          // Verify if the token is still valid after failed revocation attempt
          // This ensures the authentication state is consistent with the actual token status even if the revocation fails
          verifyToken();
        }

        return transformData;
      })
      .catch(handleNetworkError);
  };
